// LearnCore Frontend - 日历页面

import React, { useState, useEffect } from 'react';
// Layout 已由顶层 App 包裹，此处不再引入
import TaskCard from '../components/TaskCard';
import type { UserTask } from '../types/tasks-rewards';

interface CalendarDay {
  date: string;
  dayName: string;
  dayNumber: number;
  isToday: boolean;
  isWeekend: boolean;
  tasks: UserTask[];
}

const CalendarPage: React.FC = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarDays, setCalendarDays] = useState<CalendarDay[]>([]);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [tasksByDate, setTasksByDate] = useState<{ [date: string]: UserTask[] }>({});
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'month' | 'week'>('month');

  const monthNames = [
    '一月', '二月', '三月', '四月', '五月', '六月',
    '七月', '八月', '九月', '十月', '十一月', '十二月'
  ];

  const dayNames = ['日', '一', '二', '三', '四', '五', '六'];

  useEffect(() => {
    loadCalendarData();
  }, [currentDate, viewMode]);

  const loadCalendarData = async () => {
    try {
      setLoading(true);
      
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      
      if (viewMode === 'month') {
        generateMonthCalendar(year, month);
      } else {
        generateWeekCalendar(currentDate);
      }
      
      // 模拟加载任务数据
      const mockTasks: { [date: string]: UserTask[] } = {
        '2024-01-15': [
          {
            id: '1',
            user_id: '1',
            title: '完成数学作业',
            description: '完成第3章练习题',
            category: 'custom',
            type: 'daily',
            target_value: 1,
            current_value: 0,
            dkp_reward: 20,
            priority: 'high',
            status: 'pending',
            due_date: new Date('2024-01-15').getTime(),
            dkp_earned: 0,
            created_at: Date.now(),
            updated_at: Date.now()
          }
        ],
        '2024-01-16': [
          {
            id: '2',
            user_id: '1',
            title: '英语单词背诵',
            description: '背诵50个新单词',
            category: 'custom',
            type: 'daily',
            target_value: 50,
            current_value: 25,
            dkp_reward: 15,
            priority: 'medium',
            status: 'in_progress',
            due_date: new Date('2024-01-16').getTime(),
            dkp_earned: 0,
            created_at: Date.now(),
            updated_at: Date.now()
          }
        ]
      };
      
      setTasksByDate(mockTasks);
    } catch (err) {
      console.error('Failed to load calendar data:', err);
    } finally {
      setLoading(false);
    }
  };

  const generateMonthCalendar = (year: number, month: number) => {
    const firstDay = new Date(year, month, 1);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());
    
    const days: CalendarDay[] = [];
    const today = new Date();
    
    for (let i = 0; i < 42; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      
      const dateStr = date.toISOString().split('T')[0];
      
      days.push({
        date: dateStr,
        dayName: dayNames[date.getDay()],
        dayNumber: date.getDate(),
        isToday: date.toDateString() === today.toDateString(),
        isWeekend: date.getDay() === 0 || date.getDay() === 6,
        tasks: tasksByDate[dateStr] || []
      });
    }
    
    setCalendarDays(days);
  };

  const generateWeekCalendar = (date: Date) => {
    const startOfWeek = new Date(date);
    const day = startOfWeek.getDay();
    startOfWeek.setDate(startOfWeek.getDate() - day);
    
    const days: CalendarDay[] = [];
    const today = new Date();
    
    for (let i = 0; i < 7; i++) {
      const currentDay = new Date(startOfWeek);
      currentDay.setDate(startOfWeek.getDate() + i);
      
      const dateStr = currentDay.toISOString().split('T')[0];
      
      days.push({
        date: dateStr,
        dayName: dayNames[currentDay.getDay()],
        dayNumber: currentDay.getDate(),
        isToday: currentDay.toDateString() === today.toDateString(),
        isWeekend: currentDay.getDay() === 0 || currentDay.getDay() === 6,
        tasks: tasksByDate[dateStr] || []
      });
    }
    
    setCalendarDays(days);
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentDate);
    if (direction === 'prev') {
      newDate.setMonth(newDate.getMonth() - 1);
    } else {
      newDate.setMonth(newDate.getMonth() + 1);
    }
    setCurrentDate(newDate);
  };

  const navigateWeek = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentDate);
    if (direction === 'prev') {
      newDate.setDate(newDate.getDate() - 7);
    } else {
      newDate.setDate(newDate.getDate() + 7);
    }
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
    setSelectedDate(new Date().toISOString().split('T')[0]);
  };

  const getMonthStats = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    let totalTasks = 0;
    let completedTasks = 0;
    let totalDKP = 0;
    
    Object.entries(tasksByDate).forEach(([dateStr, tasks]) => {
      const date = new Date(dateStr);
      if (date >= firstDay && date <= lastDay) {
        totalTasks += tasks.length;
        completedTasks += tasks.filter(t => t.status === 'completed').length;
        totalDKP += tasks.filter(t => t.status === 'completed').reduce((sum, t) => sum + t.dkp_earned, 0);
      }
    });
    
    return { totalTasks, completedTasks, totalDKP };
  };

  if (loading) {
    return (
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-6"></div>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <div className="grid grid-cols-7 gap-2">
                {[...Array(35)].map((_, i) => (
                  <div key={i} className="h-20 bg-gray-200 dark:bg-gray-700 rounded"></div>
                ))}
              </div>
            </div>
          </div>
        </div>
    );
  }

  const stats = getMonthStats();

  return (
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* 页面标题和控件 */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {viewMode === 'month'
                ? `${currentDate.getFullYear()}年${monthNames[currentDate.getMonth()]}`
                : `${currentDate.getFullYear()}年${currentDate.getMonth() + 1}月第${Math.ceil(currentDate.getDate() / 7)}周`
              }
            </h1>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setViewMode(viewMode === 'month' ? 'week' : 'month')}
                className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                {viewMode === 'month' ? '周视图' : '月视图'}
              </button>
              <button
                onClick={goToToday}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                今天
              </button>
            </div>
          </div>

          {/* 导航控件 */}
          <div className="flex items-center justify-between">
            <button
              onClick={() => viewMode === 'month' ? navigateMonth('prev') : navigateWeek('prev')}
              className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            <div className="flex gap-4">
              <div className="text-center">
                <div className="text-sm text-gray-600 dark:text-gray-400">总任务</div>
                <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">{stats.totalTasks}</div>
              </div>
              <div className="text-center">
                <div className="text-sm text-gray-600 dark:text-gray-400">已完成</div>
                <div className="text-lg font-semibold text-green-600 dark:text-green-400">{stats.completedTasks}</div>
              </div>
              <div className="text-center">
                <div className="text-sm text-gray-600 dark:text-gray-400">获得DKP</div>
                <div className="text-lg font-semibold text-orange-600 dark:text-orange-400">{stats.totalDKP}</div>
              </div>
            </div>

            <button
              onClick={() => viewMode === 'month' ? navigateMonth('next') : navigateWeek('next')}
              className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* 日历主体 */}
          <div className="lg:col-span-3">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
              {/* 星期标题 */}
              <div className={`grid ${viewMode === 'month' ? 'grid-cols-7' : 'grid-cols-7'} bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600`}>
                {dayNames.map((day) => (
                  <div key={day} className="py-3 text-center text-sm font-medium text-gray-700 dark:text-gray-300">
                    {day}
                  </div>
                ))}
              </div>

              {/* 日期网格 */}
              <div className={`grid ${viewMode === 'month' ? 'grid-cols-7' : 'grid-cols-7'} gap-px bg-gray-200 dark:bg-gray-600`}>
                {calendarDays.map((day, index) => (
                  <div
                    key={index}
                    onClick={() => setSelectedDate(day.date)}
                    className={`bg-white dark:bg-gray-800 p-2 min-h-[80px] cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                      day.isToday ? 'bg-blue-50 dark:bg-blue-900/30' : ''
                    } ${day.isWeekend ? 'bg-gray-50 dark:bg-gray-700/50' : ''} ${
                      selectedDate === day.date ? 'ring-2 ring-blue-500 dark:ring-blue-400' : ''
                    } ${!day.isToday && viewMode === 'month' &&
                      (day.dayNumber < 1 || day.dayNumber > new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate())
                        ? 'opacity-40' : ''
                    }`}
                  >
                    <div className="flex justify-between items-start mb-1">
                      <span className={`text-sm font-medium ${
                        day.isToday ? 'text-blue-600 dark:text-blue-400' : 'text-gray-900 dark:text-gray-100'
                      } ${day.isWeekend ? 'text-red-600 dark:text-red-400' : ''}`}>
                        {day.dayNumber}
                      </span>
                      {day.tasks.length > 0 && (
                        <span className="text-xs bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400 px-1 rounded">
                          {day.tasks.length}
                        </span>
                      )}
                    </div>
                    
                    {/* 任务预览 */}
                    <div className="space-y-1">
                      {day.tasks.slice(0, 2).map((task) => (
                        <div
                          key={task.id}
                          className="text-xs p-1 bg-gray-100 dark:bg-gray-700 rounded truncate"
                          title={task.title}
                        >
                          {task.title}
                        </div>
                      ))}
                      {day.tasks.length > 2 && (
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          +{day.tasks.length - 2} 更多
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* 侧边栏 */}
          <div className="lg:col-span-1 space-y-6">
            {/* 选中日期的任务 */}
            {selectedDate && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
                <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-3">
                  {new Date(selectedDate).toLocaleDateString('zh-CN', {
                    month: 'long',
                    day: 'numeric',
                    weekday: 'long'
                  })} 的任务
                </h3>
                {tasksByDate[selectedDate]?.length > 0 ? (
                  <div className="space-y-2">
                    {tasksByDate[selectedDate].map((task) => (
                      <TaskCard
                        key={task.id}
                        task={task}
                        compact={true}
                        showActions={false}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-center text-gray-500 dark:text-gray-400 py-4">
                    <svg className="w-8 h-8 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    <div>该日期暂无任务</div>
                  </div>
                )}
              </div>
            )}

            {/* 快速统计 */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
              <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-3">本月统计</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600 dark:text-gray-400">完成率</span>
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {stats.totalTasks > 0
                      ? Math.round((stats.completedTasks / stats.totalTasks) * 100)
                      : 0}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                  <div
                    className="bg-green-600 h-2 rounded-full"
                    style={{
                      width: `${stats.totalTasks > 0
                        ? (stats.completedTasks / stats.totalTasks) * 100
                        : 0}%`
                    }}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div>
                    <div className="text-lg font-bold text-blue-600 dark:text-blue-400">{stats.totalTasks}</div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">总任务</div>
                  </div>
                  <div>
                    <div className="text-lg font-bold text-orange-600 dark:text-orange-400">{stats.totalDKP}</div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">获得DKP</div>
                  </div>
                </div>
              </div>
            </div>

            {/* 即将到期的任务 */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
              <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-3">即将到期</h3>
              <div className="space-y-2">
                {[
                  { title: '数学作业', days: 2, dkp: 20 },
                  { title: '英语单词', days: 3, dkp: 15 },
                  { title: '科学实验', days: 5, dkp: 30 }
                ].map((task, index) => (
                  <div key={index} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700 rounded">
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{task.title}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{task.days}天后到期</div>
                    </div>
                    <span className="text-sm font-medium text-orange-600 dark:text-orange-400">+{task.dkp}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
  );
};

export default CalendarPage;